import { spacing } from "@indeed/ifl-tokens/react-native";
import { type FC, Fragment, type ReactNode } from "react";
import {
  FlatList,
  type ListRenderItemInfo,
  Linking,
  Platform,
  StyleSheet,
  type StyleProp,
  type TextStyle,
} from "react-native";

import { Box } from "../../../mads/Box";
/**
 * RichText: parses markdown-like content and renders it. Block-level splits happen at
 * headings (different line height) and bullet lists (rendered via MADS List for correct
 * semantics and styling). See RichTextFormatterUtils for the tokenizer.
 */
import type { BoxStyles } from "../../../mads/Box/Box";
import { Flex } from "../../../mads/Flex";
import { Heading } from "../../../mads/Heading";
import { List } from "../../../mads/List";
import type { ListItem } from "../../../mads/List/List.types";
import { Text } from "../../../mads/Text";
import type { TextProps } from "../../../mads/Text/Text";
import { getFontSizeStyle, type TextLevel } from "../../../mads/Text/TextLevel";
import { TextLink } from "../../../mads/TextLink";
import { parseRichTextContent, type RichTextToken } from "./RichTextFormatterUtils";

/** Props forwarded to every Text/Heading block so variant and accessibility apply to all content. */
type BlockTextProps = Pick<
  TextProps,
  "variant" | "accessible" | "selectable" | "ariaLabel" | "ariaHidden" | "accessibilityRole"
>;

export interface RichTextProps extends Omit<TextProps, "children"> {
  /**
   * The rich string content to render (supports markdown-like formatting)
   */
  content: string;
  /**
   * Whether to add underlines to links
   */
  underlinedLinks?: boolean;
  /**
   * Custom handler for link presses (if not provided, uses Linking.openURL)
   */
  onLinkPress?: (url: string) => void;
  /**
   * Error message to display if rich string processing fails
   */
  errorMessage?: string;
  /**
   * When true, each bullet renders as a grouped accessible element (marker + text
   * as one focus stop) with accessibilityLabel for position announcements,
   * instead of using the MADS List component.
   */
  groupedBulletA11y?: boolean;
}

function renderCommonToken(
  token: RichTextToken,
  key: string,
  onLinkPress?: (url: string) => void,
  textStyle?: StyleProp<TextStyle>,
  blockProps?: BlockTextProps,
): ReactNode {
  switch (token.type) {
    case "text":
      return <Fragment key={key}>{token.content}</Fragment>;
    case "bold":
      return (
        <Text key={key} sx={[textStyle, { fontWeight: "bold" }]} {...blockProps}>
          {token.content}
        </Text>
      );
    case "italic":
      return (
        <Text key={key} sx={[textStyle, { fontStyle: "italic" }]} {...blockProps}>
          {token.content}
        </Text>
      );
    case "strikethrough":
      return (
        <Text key={key} sx={[textStyle, { textDecorationLine: "line-through" }]} {...blockProps}>
          {token.content}
        </Text>
      );
    case "underline":
      return (
        <Text key={key} sx={[textStyle, { textDecorationLine: "underline" }]} {...blockProps}>
          {token.content}
        </Text>
      );
    case "link":
      return (
        <TextLink key={key} variant="neutral" onPress={() => onLinkPress?.(token.url ?? "")}>
          {token.content}
        </TextLink>
      );
    default:
      return null;
  }
}

/**
 * Renders one run of tokens (no headings) as a single Text. Newlines render as \n.
 * Explicit lineHeight fixes Android clipping (#30272, #53344).
 */
function renderRun(
  runTokens: RichTextToken[],
  keyPrefix: string,
  onLinkPress: ((url: string) => void) | undefined,
  getStyleForLevel: (level: TextLevel) => StyleProp<TextStyle>,
  baseLevel: TextLevel,
  blockProps?: BlockTextProps,
): ReactNode {
  if (runTokens.length === 0) return null;
  const bodyStyle = getStyleForLevel(baseLevel);
  const children: ReactNode[] = runTokens.map((token, idx) => {
    if (token.type === "linebreak") return <Fragment key={`${keyPrefix}-${idx}`}>{"\n"}</Fragment>;
    return renderCommonToken(token, `${keyPrefix}-${idx}`, onLinkPress, bodyStyle, blockProps);
  });
  return (
    <Text key={keyPrefix} level={baseLevel} sx={bodyStyle} {...blockProps}>
      {children}
    </Text>
  );
}

type ListMarkerType = "bullet" | "numbered";

/**
 * Groups consecutive list tokens into logical list items. Each item starts with a
 * marker token (bullet or numbered) and may be followed by inline tokens (bold,
 * italic, link, etc.) that share the same indentLevel.
 */
function groupListItems(
  listTokens: RichTextToken[],
  markerType: ListMarkerType,
): RichTextToken[][] {
  const items: RichTextToken[][] = [];
  let current: RichTextToken[] = [];

  for (const token of listTokens) {
    if (token.type === "linebreak") continue;
    if (token.type === markerType) {
      if (current.length > 0) items.push(current);
      current = [token];
    } else {
      current.push(token);
    }
  }
  if (current.length > 0) items.push(current);

  return items;
}

/**
 * Renders a group of consecutive list tokens as a MADS List with proper
 * accessibility roles and marker styling. Supports both bullet (\u2022) and
 * numbered (1., 2., …) markers.
 */
function renderList(
  listTokens: RichTextToken[],
  markerType: ListMarkerType,
  keyPrefix: string,
  onLinkPress: ((url: string) => void) | undefined,
  getStyleForLevel: (level: TextLevel) => StyleProp<TextStyle>,
  baseLevel: TextLevel,
  blockProps?: BlockTextProps,
): ReactNode {
  const items = groupListItems(listTokens, markerType);
  const bodyStyle = getStyleForLevel(baseLevel);

  const listData: ListItem[] = items.map((itemTokens, itemIdx) => {
    const marker =
      markerType === "bullet" ? "\u2022" : `${itemTokens[0]?.listNumber ?? itemIdx + 1}.`;

    return {
      key: `${keyPrefix}-item-${itemIdx}`,
      content: (
        <Flex
          sx={{
            flexDirection: "row",
            gap: spacing.s2,
            // Compensate for the 8px height of MADS VisuallyHidden position
            // announcements that follow each item content in the List component.
            marginBottom: -8,
            paddingLeft: (itemTokens[0]?.indentLevel ?? 0) * spacing.s4,
          }}
        >
          <Box sx={{ flexShrink: 0 }} aria-hidden={true} importantForAccessibility="no">
            <Text level={baseLevel} sx={bodyStyle} {...blockProps}>
              {marker}
            </Text>
          </Box>
          <Flex sx={{ flexShrink: 1 }}>
            <Text level={baseLevel} sx={bodyStyle} {...blockProps}>
              {itemTokens.map((token, idx) => {
                if (token.type === markerType) {
                  return token.content ? (
                    <Fragment key={`${keyPrefix}-${itemIdx}-${idx}`}>{token.content}</Fragment>
                  ) : null;
                }
                return renderCommonToken(
                  token,
                  `${keyPrefix}-${itemIdx}-${idx}`,
                  onLinkPress,
                  bodyStyle,
                  blockProps,
                );
              })}
            </Text>
          </Flex>
        </Flex>
      ),
    };
  });

  return (
    <List
      key={keyPrefix}
      data={listData}
      scrollEnabled={false}
      style={{ paddingLeft: spacing.s4, marginTop: -8, marginBottom: spacing.s2 }}
    />
  );
}

interface GroupedBulletItem {
  key: string;
  tokens: RichTextToken[];
  marker: string;
  a11yLabel: string | undefined;
  indentLevel: number;
}

/**
 * Renders bullets as individually grouped accessible elements using FlatList.
 * FlatList (native ScrollView) breaks through a parent's accessible={true} grouping,
 * allowing the parent to be focusable as "list" while items remain individually navigable.
 * Each bullet is a single focus stop (marker + text + position via accessibilityLabel).
 */
function renderGroupedBulletList(
  listTokens: RichTextToken[],
  markerType: ListMarkerType,
  keyPrefix: string,
  onLinkPress: ((url: string) => void) | undefined,
  getStyleForLevel: (level: TextLevel) => StyleProp<TextStyle>,
  baseLevel: TextLevel,
  blockProps?: BlockTextProps,
): ReactNode {
  const items = groupListItems(listTokens, markerType);
  const bodyStyle = getStyleForLevel(baseLevel);
  const isAndroid = Platform.OS === "android";
  const totalItems = items.length;

  const listData: GroupedBulletItem[] = items.map((itemTokens, itemIdx) => {
    const marker =
      markerType === "bullet" ? "\u2022" : `${itemTokens[0]?.listNumber ?? itemIdx + 1}.`;
    const plainText = itemTokens
      .map((t) => t.content)
      .filter(Boolean)
      .join("");

    return {
      key: `${keyPrefix}-item-${itemIdx}`,
      tokens: itemTokens,
      marker,
      a11yLabel: isAndroid ? `${itemIdx + 1} of ${totalItems}. ${plainText}` : undefined,
      indentLevel: itemTokens[0]?.indentLevel ?? 0,
    };
  });

  const renderItem = ({ item }: ListRenderItemInfo<GroupedBulletItem>) => (
    <Box accessible={isAndroid} accessibilityLabel={item.a11yLabel}>
      <Flex
        sx={{
          flexDirection: "row",
          gap: spacing.s2,
          paddingLeft: item.indentLevel * spacing.s4,
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Text level={baseLevel} sx={bodyStyle} {...blockProps}>
            {item.marker}
          </Text>
        </Box>
        <Flex sx={{ flexShrink: 1 }}>
          <Text level={baseLevel} sx={bodyStyle} {...blockProps}>
            {item.tokens.map((token, idx) => {
              if (token.type === markerType) {
                return token.content ? (
                  <Fragment key={`${item.key}-${idx}`}>{token.content}</Fragment>
                ) : null;
              }
              return renderCommonToken(
                token,
                `${item.key}-${idx}`,
                onLinkPress,
                bodyStyle,
                blockProps,
              );
            })}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );

  return (
    <Box key={keyPrefix} sx={groupedListStyles.container} role={isAndroid ? "list" : undefined}>
      <FlatList data={listData} renderItem={renderItem} scrollEnabled={false} />
    </Box>
  );
}

const groupedListStyles = StyleSheet.create({
  container: {
    paddingLeft: spacing.s4,
    marginBottom: spacing.s2,
  },
});

/** Returns true if the token belongs to any list (bullet/numbered or its inline continuation). */
function isListRelated(token: RichTextToken): boolean {
  return token.type === "bullet" || token.type === "numbered" || token.indentLevel != null;
}

/**
 * Split tokens at headings and lists; render each run as one Text (normal newlines),
 * each heading as Heading, and each list group (bullet or numbered) as a MADS List.
 */
function renderBlocks(
  tokens: RichTextToken[],
  onLinkPress: ((url: string) => void) | undefined,
  getStyleForLevel: (level: TextLevel) => StyleProp<TextStyle>,
  baseLevel: TextLevel,
  blockProps?: BlockTextProps,
  groupedBulletA11y = false,
): ReactNode[] {
  const blocks: ReactNode[] = [];
  let run: RichTextToken[] = [];
  let listRun: RichTextToken[] = [];
  let activeListType: ListMarkerType | null = null;
  let key = 0;
  let justAfterHeading = false;

  const flushRun = () => {
    while (
      run.length > 1 &&
      run[run.length - 1].type === "linebreak" &&
      run[run.length - 2].type === "linebreak"
    )
      run.pop();
    while (run.length > 1 && run[0].type === "linebreak" && run[1].type === "linebreak")
      run.shift();
    if (run.length === 0) return;
    if (!run.some((t) => t.type !== "linebreak")) {
      run = [];
      return;
    }
    const node = renderRun(
      run,
      `run-${key++}`,
      onLinkPress,
      getStyleForLevel,
      baseLevel,
      blockProps,
    );
    if (node != null) blocks.push(node);
    run = [];
  };

  const flushListRun = () => {
    if (listRun.length > 0 && activeListType) {
      const prefix = activeListType === "bullet" ? "blist" : "nlist";
      const listKey = `${prefix}-${key++}`;
      const node = groupedBulletA11y
        ? renderGroupedBulletList(
            listRun,
            activeListType,
            listKey,
            onLinkPress,
            getStyleForLevel,
            baseLevel,
            blockProps,
          )
        : renderList(
            listRun,
            activeListType,
            listKey,
            onLinkPress,
            getStyleForLevel,
            baseLevel,
            blockProps,
          );
      blocks.push(node);
      listRun = [];
      activeListType = null;
    }
  };

  const isListMarker = (t: RichTextToken) => t.type === "bullet" || t.type === "numbered";

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    const nextToken = tokens[i + 1];

    if (token.type === "heading") {
      const runHasContent = run.some((t) => t.type !== "linebreak");
      if (runHasContent && run.length > 0 && run[run.length - 1].type === "linebreak") run.pop();
      flushRun();
      flushListRun();
      const markdownLevel = token.headingLevel ?? 1;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      const headingLevel = (9 - markdownLevel) as TextLevel;
      const headingStyle = getStyleForLevel(headingLevel);
      // Pass as a `StyleSheet.flatten([...])` array (not a spread-in-object-literal
      // composition) so the mads→mads-strict codemod can unwrap the flatten cleanly
      // into a strict-compatible sx array (Case 7b in codemod-transform-styles).
      // Runtime semantics are identical: `StyleSheet.flatten` accepts an array of
      // `StyleProp<TextStyle>` and produces the same merged style either way.
      // The explicit `TextStyle` annotation is dropped so TS infers the post-codemod
      // `StyleXStyles[]` shape in the strict copy without conflicting with the
      // pre-codemod `TextStyle` return type. See RN-3429 / RN-3428 patterns.
      const headingSx = StyleSheet.flatten([headingStyle, { marginBottom: 0 }]);
      blocks.push(
        <Heading key={`h-${key++}`} level={headingLevel} sx={headingSx} {...blockProps}>
          {token.content}
        </Heading>,
      );
      justAfterHeading = true;
    } else if (isListMarker(token) || (listRun.length > 0 && isListRelated(token))) {
      if (isListMarker(token) && activeListType && token.type !== activeListType) {
        flushListRun();
      }
      if (run.length > 0) {
        const runHasContent = run.some((t) => t.type !== "linebreak");
        if (runHasContent && run[run.length - 1].type === "linebreak") run.pop();
        flushRun();
      }
      if (!activeListType && isListMarker(token)) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        activeListType = token.type as ListMarkerType;
      }
      justAfterHeading = false;
      listRun.push(token);
    } else if (token.type === "linebreak" && listRun.length > 0) {
      const nextIsListMarker = nextToken && isListMarker(nextToken);
      if (nextIsListMarker && nextToken.type === activeListType) {
        listRun.push(token);
      } else {
        flushListRun();
        run.push(token);
      }
    } else {
      flushListRun();
      if (
        justAfterHeading &&
        run.length === 0 &&
        token.type === "linebreak" &&
        nextToken?.type !== "heading"
      ) {
        justAfterHeading = false;
        continue;
      }
      justAfterHeading = false;
      run.push(token);
    }
  }
  flushRun();
  flushListRun();

  return blocks.filter(Boolean);
}

/**
 * Renders rich string content using React Native Text components
 * Similar to Swift's StringFormattingUtils.createMarkdownAttributedString
 *
 * Features:
 * - Headings: # H1, ## H2, ### H3, etc. (up to H6)
 * - Unordered lists: * item or - item (both convert to •)
 * - Ordered lists: 1. item, 2. item, etc.
 * - Links: [text](url) (converts to pressable text)
 * - Bold text: **text**
 * - Italic text: *text* or _text_
 * - Strikethrough text: ~~text~~
 * - Underline text: <u>text</u>
 * - Line breaks: <br> or <br/>
 */
export const RichText: FC<RichTextProps> = ({
  content,
  level = 3,
  onLinkPress = (url: string) => {
    // TODO: replace with TAER api to open company insights and external urls inside the app
    Linking.openURL(url).catch((err) => {
      console.warn("Failed to open URL:", err);
    });
  },
  errorMessage,
  groupedBulletA11y,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  underlinedLinks,
  ...textProps
}) => {
  // RN Android bug: Text clips last line when lineHeight comes only from theme (#30272, #53344).
  // Explicitly set lineHeight per level from the design system (TextLevel: normal for 1-4, tight for 5-8).
  const getStyleForLevel = (tokenLevel: TextLevel): StyleProp<TextStyle> => [
    textProps.sx,
    { lineHeight: getFontSizeStyle(tokenLevel).lineHeight },
  ];

  const baseLevel: TextLevel = level ?? 3;

  const blockProps: BlockTextProps = {
    variant: textProps.variant,
    accessible: textProps.accessible,
    selectable: textProps.selectable,
    ariaLabel: textProps.ariaLabel,
    ariaHidden: textProps.ariaHidden,
    accessibilityRole: textProps.accessibilityRole,
  };

  try {
    const tokens = parseRichTextContent(content);
    const blocks = renderBlocks(
      tokens,
      onLinkPress,
      getStyleForLevel,
      baseLevel,
      blockProps,
      groupedBulletA11y,
    );

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    return <Box sx={textProps.sx as BoxStyles}>{blocks}</Box>;
  } catch (error) {
    console.error("Error rendering rich string:", error);

    if (errorMessage) {
      return (
        <Text {...textProps} level={baseLevel} sx={getStyleForLevel(baseLevel)}>
          {errorMessage}
        </Text>
      );
    }

    return null;
  }
};
